import { Component, HostListener, OnInit, inject, isDevMode } from '@angular/core';
import { SocketService } from './services/socket.service';
import { Title } from '@angular/platform-browser';
import { environment as env } from '../environments/environment';
import { TranslocoService, getBrowserLang } from '@ngneat/transloco';
import * as storageHelper from '../app/modules/core/helpers/storage-helper';
import { PwaCheckUpdateService } from './modules/core/services/pwa-check-update.service';
import { PwaLogUpdateService } from './modules/core/services/pwa-log-update.service';
import { PwaPromptUpdateService } from './modules/core/services/pwa-prompt-update.service';
import { PwaRecoverService } from './modules/core/services/pwa-recover.service';
import { LogLevel, LoggingService } from './services/logging.service';
import { MatDialog } from '@angular/material/dialog';
import { LoggingSetupDialogComponent } from './components/logging-setup-dialog/logging-setup-dialog.component';
import { PwaPromptInstallService } from './modules/core/services/pwa-prompt-install.service';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrl: './app.component.scss',
    standalone: false
})
export class AppComponent implements OnInit {
  static readonly COMPONENT_NAME = 'AppComponent';

  socketService = inject(SocketService);
  titleService = inject(Title);
  translocoService = inject(TranslocoService);
  pwaCheckUpdateService = inject(PwaCheckUpdateService);
  pwaLogUpdateService = inject(PwaLogUpdateService);
  pwaPromptUpdateService = inject(PwaPromptUpdateService);
  pwaRecoverService = inject(PwaRecoverService);
  dialog = inject(MatDialog);
  loggingService = inject(LoggingService);
  pwaPromptInstallService = inject(PwaPromptInstallService);

  canSaveLanguageChange = false;

  showLoggingSetupButton = false;

  ngOnInit(): void {
    this.titleService.setTitle(`Cantrak (v${env.appVersion})`);

    this.socketService.init();

    this.setUpLanguage();

    this.setPwaServices();

    window.addEventListener("load", function () {
      setTimeout(function () {
        // Hide the address bar!
        window.scrollTo(0, 1);
      }, 0);
    });

  }

  @HostListener('window:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent) {
    // Check if Alt + m is pressed
    if (event.altKey && event.key === 'm') {
      // Wait for 'l' or 's' key press
      this.loggingService.log(LogLevel.DEBUG, AppComponent.COMPONENT_NAME, 'Waiting for next seq key...');
      const waitForG = (e: KeyboardEvent) => {
        if (e.key === 'l') {
          this.loggingService.log(LogLevel.DEBUG, AppComponent.COMPONENT_NAME, 'Enabling Log setting...');
          this.showLoggingSetupButton = !this.showLoggingSetupButton; // Toggle the button visibility
          window.removeEventListener('keydown', waitForG); // Remove the listener
        } else if (e.key === 's') {
          this.loggingService.log(LogLevel.DEBUG, AppComponent.COMPONENT_NAME, 'Enabling Log setting...');
          this.showLoggingSetupButton = !this.showLoggingSetupButton; // Toggle the button visibility
          window.removeEventListener('keydown', waitForG); // Remove the listener
          this.openLoggingSettingsUiDialog();
        }
      };

      window.addEventListener('keydown', waitForG);
    }
  }

  @HostListener("window:visibilitychange", ["$event"])
  @HostListener('window:focus', ['$event'])
  onFocus(event: any): void {
    this.socketService.verifyConnectionStatus();
  }

  @HostListener('window:beforeinstallprompt', ['$event'])
  onBeforeInstallPrompt(event: Event) {
    event.preventDefault();
    this.loggingService.log(LogLevel.DEBUG, AppComponent.COMPONENT_NAME, 'Before Install Prompt Event', event)
    this.pwaPromptInstallService.setDeferredPWAInstallPromptObs(event);
  }


  setUpLanguage() {
    this.translocoService.langChanges$.subscribe(lang => {
      if (this.canSaveLanguageChange) {
        this.loggingService.log(LogLevel.DEBUG, AppComponent.COMPONENT_NAME, 'App Language Changed: Saving...', lang);
        // Do not modify, until first attempt to read from localStorage is made...
        storageHelper.setToLocalStorageWithExpiry('user_preferred_language', lang, 604800);
      }
    });

    // Check if user have selected different preferred language..
    let userLang = storageHelper.getFromLocalStorageWithExpiry('user_preferred_language');
    if (!userLang || userLang == '') {
      userLang = getBrowserLang();
    }

    this.translocoService.setActiveLang(userLang);  // Setting the default browser language..
    this.canSaveLanguageChange = true;
  }



  setPwaServices() {
    if (env.production || env.stage) {
      this.loggingService.log(LogLevel.DEBUG, AppComponent.COMPONENT_NAME, '[PWA]: Initializing PWA Services...');
      this.pwaCheckUpdateService.init();
      this.pwaLogUpdateService.init();
      this.pwaPromptUpdateService.init();
      this.pwaRecoverService.init();
    } else {
      this.loggingService.log(LogLevel.DEBUG, AppComponent.COMPONENT_NAME, '[PWA]: Not Initializing PWA Services...');
    }
  }


  openLoggingSettingsUiDialog() {
    const dialogRef = this.dialog.open(LoggingSetupDialogComponent, {
      minWidth: "365px",
      width: "50%",
      panelClass: ['tis-simple-confirmation'],
      data: {},
      disableClose: false,
    });

    dialogRef.afterClosed().subscribe((result) => {
      this.loggingService.log(LogLevel.DEBUG, AppComponent.COMPONENT_NAME, 'The LoggingSetupDialogComponent dialog was closed with result:', result);
    });
  }



}
